import {
	Component,
	OnInit,
	QueryList,
	ViewChildren,
	OnDestroy,
} from '@angular/core';
import { map, takeUntil } from 'rxjs/operators';
import { FormBuilder, FormControlName, FormGroup } from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { Subject } from 'rxjs';
import { FormConfig } from 'src/shared/form-models/form-cva.component';
import { preventFlicker } from 'src/shared/utilities/custom-rxjs-operators';

import * as FieldTeamDetailSelectors from './+state/field-team-detail.selectors';
import * as FieldTeamDetailActions from './+state/field-team-detail.actions';
import { FieldTeamDetailPartialState } from './+state/field-team-detail.reducer';
import { Store, select } from '@ngrx/store';
import { FieldTeamDetail } from './field-team-detail.models';
import { fieldTeamToFieldTeamForm } from './field-team-detail.utility';
import { collectTouchableChildControls } from 'src/shared/utilities/utility-functions';
import { LookUpsService } from 'src/shared/lookups.service';
import { AppState } from 'src/admin-app/shared/+state';
import { ClearFieldWorkers } from 'src/admin-app/jobs/job-assignments/+state/field-worker/field-worker.actions';

const teamDetailsFormConfig: FormConfig<FieldTeamDetail> = {
	generalInfo: [''],
	members: [''],
};

@Component({
	templateUrl: './field-team-detail.component.html',
})
export class FieldTeamDetailComponent
	extends SelfDestruct
	implements OnInit, OnDestroy
{
	@ViewChildren(FormControlName) childControls: QueryList<FormControlName>;

	private _invalidSubmission = new Subject<number>();
	form = this.fb.group(teamDetailsFormConfig) as FormGroup;

	readonly id$ = this.fieldTeamStore.pipe(
		select(FieldTeamDetailSelectors.getSelectedFieldTeamId)
	);

	readonly title$ = this.fieldTeamStore.pipe(
		select(FieldTeamDetailSelectors.getTitle)
	);

	readonly isNewRecord$ = this.id$.pipe(map((id) => id === 0));

	readonly isLoading$ = this.fieldTeamStore.pipe(
		select(FieldTeamDetailSelectors.getLoadingStatus)
	);

	readonly isSaving$ = this.fieldTeamStore.pipe(
		select(FieldTeamDetailSelectors.getIsSaving),
		preventFlicker(1000)
	);

	readonly invalidSubmission$ = this._invalidSubmission.asObservable();

	constructor(
		private readonly fieldTeamStore: Store<FieldTeamDetailPartialState>,
		private readonly fb: FormBuilder,
		private readonly lookUpsService: LookUpsService,
		private store: Store<AppState>
	) {
		super();
	}

	ngOnInit() {
		this.fieldTeamStore
			.pipe(
				select(FieldTeamDetailSelectors.getSelectedFieldTeam),
				map(fieldTeamToFieldTeamForm),
				takeUntil(this.destroyed)
			)
			.subscribe((formValue) => {
				this.form.patchValue(formValue, { emitEvent: false });
			});
	}

	save(stay = false) {
		if (this.form.valid) {
			const formValue = this.form.value;
			this.fieldTeamStore.dispatch(
				FieldTeamDetailActions.saveRequested({
					shouldStayOnPage: stay,
					formValue,
				})
			);
		} else {
			const controls = collectTouchableChildControls(this.childControls);
			controls.forEach((c) => c.onTouched());
			this._invalidSubmission.next(new Date().getTime());
		}
	}

	ngOnDestroy() {
		this.lookUpsService.refreshLookups();
		this.store.dispatch(new ClearFieldWorkers());
		this.fieldTeamStore.dispatch(
			FieldTeamDetailActions.fieldTeamDetailDestroyed()
		);
		super.ngOnDestroy();
	}
}
